/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import GateLogCard from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/GateLogCard";
import {
    IconDocumentVehicleGate,
    IconVehicleInMotion
} from "../../../../../../../components/buttons/IconButtons";
import CustomDateTimePicker from "../../../../../../../components/CustomDateTimePicker";
import MaterialsInOut
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/PlantMaterialControl/MaterialsInOut";
import {
    MOVEMENT_DIRECTION,
    PLANT_MATERIAL_CONTROL_ITEM_MODEL,
    buildEntryAndExitFormItem,
    getMovementDirectionOption,
    getMovementDirectionOptionByValue
} from "../../../../../MaterialControl/Model/Model";
import GuardGateLogSection from "./GuardGateLogSection";
import GuardTransportSection from "./GuardTransportSection";
import ButtonIcon from "../../../../../../../components/buttons/ButtonIcon";
import {CleanIcon} from "../../../../../../../components/buttons/IconButtons";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {isNotEmpty} from "../../../../../../../Apis/functions";
import { PERIOD_TIME_UNIT_ENUM } from "../../../../../../../enums/PeriodTimeUnitEnum";

/**
 * Gate log the guard completes when a plant material control movement is
 * already at the plant and only waits for its dispatch.
 *
 * The transport is the one the requester registered, so it is only read; what
 * the guard captures is what was left blank until the movement happened: the
 * date of the direction of the movement and the materials that came in or went
 * out of the plant.
 *
 * A movement that is already closed has nothing left to capture, so the date is
 * only read.
 *
 * @param {Object} props - Values of the gate log and its handlers.
 * @param {Function} props.t - Translation handler.
 * @param {Object} props.item - Folio the movement belongs to.
 * @param {Object} props.gateLog - Gate log captured by the guard.
 * @param {Function} props.setGateLog - State updater of the gate log.
 * @param {Function} props.handleChange - Change handler of the fields of the gate log.
 * @param {Object} props.photoPicker - Evidence of the movement captured at the gate.
 * @param {Function} props.setPhotoPicker - State updater of the evidence of the gate.
 * @param {boolean} props.emptyFields - Whether the required fields are marked.
 * @param {boolean} props.emptyFieldsItems - Whether the required fields of the materials are marked.
 * @param {Function} props.setEmptyFieldsItems - State updater of the marked fields of the materials.
 * @param {boolean} props.disabled - Whether the gate log is only read.
 * @param {string} props.title - Title the card of the log is named with.
 * @param {string} props.notePlaceholder - Placeholder that says what the note holds.
 * @param {boolean} props.showMaterials - Whether the materials of the movement
 * are read, because a folio reported without dispatching them goes back to the
 * requester and the gate has nothing left to validate on it.
 * @param {boolean} props.showTransport - Whether the transport is read with the
 * log, because the vehicle is captured in the moment it is at the gate and a
 * folio that already read it there does not open it again.
 * @param {boolean} props.transportDisabled - Whether the transport is only read,
 * because the vehicle was already written on the step before this one.
 * @param {Object} props.contentRef - Reference of the content of the card.
 *
 * @returns {JSX.Element} Card with the transport, the date and the materials of the gate.
 */
const GuardDispatchGateLogSection = ({
                                         t,
                                         item,
                                         gateLog,
                                         setGateLog,
                                         handleChange,
                                         photoPicker,
                                         setPhotoPicker,
                                         emptyFields,
                                         emptyFieldsItems,
                                         setEmptyFieldsItems,
                                         disabled = false,
                                         title = 'gate_log',
                                         notePlaceholder,
                                         showMaterials = true,
                                         showTransport = true,
                                         transportDisabled = false,
                                         contentRef
                                     }) => {

    // The material of a plant material control leaves the plant before it comes
    // back, so the exit is the direction of the movement while the folio does
    // not return one.
    const selectedDirectionOption = getMovementDirectionOptionByValue(item?.movementDirection)
        || getMovementDirectionOption(MOVEMENT_DIRECTION.EXIT);
    const SelectedDirectionIcon = selectedDirectionOption?.Icon;

    // The transport is optional for the requester, so only what already travels
    // with the folio is read; what was left blank is completed at the gate
    const isCarrierRegistered = isNotEmpty(item?.carrierCompany);
    const isVehicleRegistered = isNotEmpty(item?.vehicleInformation);
    // The date of the movement is optional the same way, so a folio that was
    // registered with it is only read and the gate writes the date of the
    // movement only when the requester left it blank
    const isDateRegistered = isNotEmpty(item?.expectedMovementDate);

    // The materials that travel with the folio are only read at the gate, so
    // the list is written only while none of them was registered with it
    const canWriteMaterials = showMaterials
        && !(gateLog?.items || []).some((e) => e?.registeredId);

    // The log is written while the folio waits for its dispatch, so that is the
    // only moment it is cleared, and only when something in it is still being
    // captured: a folio that travels with the whole transport and with its
    // materials has nothing the gate can clear.
    const canWriteTransport = showTransport
        && !transportDisabled
        && (!isCarrierRegistered || !isVehicleRegistered);
    const hasEditableFields = !disabled
        && (canWriteTransport || !isDateRegistered || canWriteMaterials);
    const canClear = item?.dispatchStatusId === STATUS_MODEL.PENDING_TO_DISPATCH && hasEditableFields;

    /**
     * Clears what the guard is writing in the log.
     *
     * What the requester already registered travels with the folio, so it is
     * only read at the gate and stays as it was; only the fields the guard
     * completes are cleared, and the materials go back to the empty one the log
     * opens with.
     *
     * @returns {void}
     */
    const handleClear = () => {
        setEmptyFieldsItems(false);
        setGateLog((prev) => ({
            ...prev,
            ...(!canWriteTransport || isCarrierRegistered ? {} : {carrierCompany: ''}),
            ...(!canWriteTransport || isVehicleRegistered ? {} : {vehicleInformation: ''}),
            ...(isDateRegistered ? {} : {[selectedDirectionOption.dateName]: null}),
            ...(canWriteMaterials
                ? {items: [buildEntryAndExitFormItem(PLANT_MATERIAL_CONTROL_ITEM_MODEL)]}
                : {})
        }));
    };

    return (
        <>
        <GateLogCard
            t={t}
            Icon={IconDocumentVehicleGate}
            title={title}
            contentRef={contentRef}
            action={canClear &&
                <ButtonIcon
                    id='btn_gdgls_clean_fields'
                    Icon={CleanIcon}
                    onClick={handleClear}
                    title={'clear_instances'}
                    className={"btn-clean-secondary-600"}
                    iconProps='btn-icon-svg-12px icon-color-general-00'
                />
            }
        >
            <div className={"px-3"}>
                {/* The vehicle is read in the moment it is at the gate, so the
                    folio that already captured it there reads it in its
                    information and no longer with the log of the dispatch. */}
                {showTransport &&
                    <GuardTransportSection
                        t={t}
                        item={item}
                        gateLog={gateLog}
                        handleChange={handleChange}
                        emptyFields={emptyFields}
                        disabled={disabled || transportDisabled}
                    />
                }

                <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
                    <div className={"d-flex align-items-center p-2"}>
                        <div className={`mr-2 ${selectedDirectionOption.iconBackground} p-2 border-radius-8px d-flex align-items-center`}>
                            <SelectedDirectionIcon className={selectedDirectionOption.iconColor}/>
                        </div>
                        <p className={"fs-14px ibl-font-w-600"}>{t(selectedDirectionOption.trans)}</p>
                    </div>
                    <div className={"border-bottom-shades-300"}/>

                    <div className={"row input-padding-top pb-3"}>
                        <div className={"col-12"}>
                            <CustomDateTimePicker
                                id={`dp_gdgls_${selectedDirectionOption.dateTrans}`}
                                name={selectedDirectionOption.dateName}
                                onChange={(e) => {
                                    handleChange({target: {value: e, name: selectedDirectionOption.dateName}});
                                }}
                                trans={selectedDirectionOption.dateTrans}
                                value={gateLog?.[selectedDirectionOption.dateName]}
                                required={!disabled && !isDateRegistered}
                                disabled={disabled || isDateRegistered}
                                openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                                disablePast={true}
                                emptyFields={emptyFields}
                            />
                        </div>

                        {showMaterials &&
                            <div className={"col-12"}>
                                <MaterialsInOut
                                    emptyFields={emptyFields}
                                    materialRequest={gateLog}
                                    setMaterialRequest={setGateLog}
                                    emptyFieldsItems={emptyFieldsItems}
                                    setEmptyFieldsItems={setEmptyFieldsItems}
                                    required={false}
                                    disabled={disabled}
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>
        </GateLogCard>

        {/* What closes the movement is registered apart from what the gate
            wrote of it, so the dispatch is captured in a card of its own, and
            the folio that was already dispatched keeps reading there what the
            guard wrote when it closed the movement. */}
        <div className={"mt-3"}>
            <GuardGateLogSection
                t={t}
                title={'dispatch_details'}
                evidenceCard={false}
                noteTrans={'dispatch_note'}
                gateLog={gateLog}
                handleChange={handleChange}
                photoPicker={photoPicker}
                setPhotoPicker={setPhotoPicker}
                emptyFields={emptyFields}
                disabled={disabled}
                icon={IconVehicleInMotion}
                notePlaceholder={notePlaceholder}
            />
        </div>
        </>
    );
};

export default GuardDispatchGateLogSection;
